import React, { useMemo, useState } from 'react';
import { BsDatabaseFillAdd } from 'react-icons/bs';
import { FaBrain, FaEnvelope } from 'react-icons/fa';
import { FiMessageSquare } from 'react-icons/fi';
import { LuAlertTriangle, LuFileJson, LuGripVertical, LuPanelLeftClose, LuPanelLeftOpen, LuSearch, LuTags } from 'react-icons/lu';
import { MdOutlineSupportAgent, MdOutlineWebhook } from 'react-icons/md';
import { HiOutlineSave } from 'react-icons/hi';
import { TiFlowMerge } from 'react-icons/ti';
import { FaArrowRightArrowLeft } from 'react-icons/fa6';

const onDragStart = (event, nodeType) => {
  event.dataTransfer.setData('application/reactflow', nodeType);
  event.dataTransfer.effectAllowed = 'move';
};

const CATEGORIES = [
  {
    label: 'Respuestas',
    items: [
      { type: 'BasicAnswer', label: 'Respuestas básicas', icon: FiMessageSquare, color: '#00A884' },
      { type: 'SaveAnswer', label: 'Guardar respuestas', icon: HiOutlineSave, color: '#D89C7E' },
      { type: 'Tags', label: 'Etiquetas', icon: LuTags, color: '#5BC0DE' },
    ],
  },
  {
    label: 'Lógica',
    items: [
      { type: 'Conditions', label: 'Condiciones', icon: FaArrowRightArrowLeft, color: '#E5253D' },
    ],
  },
  {
    label: 'Integraciones',
    items: [
      { type: 'QueryApi', label: 'Consulta API', icon: BsDatabaseFillAdd, color: '#7ECCD8' },
      { type: 'WebHook', label: 'WebHook', icon: MdOutlineWebhook, color: '#FC6530' },
      { type: 'Json', label: 'JSON', icon: LuFileJson, color: '#A7ADA7' },
    ],
  },
  {
    label: 'Comunicación',
    items: [
      { type: 'TransferAgent', label: 'Transferir a área o agente', icon: MdOutlineSupportAgent, color: '#F56F89' },
      { type: 'FlowsWhatsApp', label: 'Flujos de WhatsApp', icon: TiFlowMerge, color: '#B85C7C' },
      { type: 'AssitantAI', label: 'Asistente con IA', icon: FaBrain, color: '#A7ADA7' },
      { type: 'Template', label: 'Plantilla', icon: FaEnvelope, color: '#00A884' },
    ],
  },
];

const PALETTE_CSS = `
.su-pal { position: absolute; left: 14px; top: 14px; z-index: 100; display: flex; flex-direction: column; max-height: calc(100% - 28px); overflow: hidden; border: 1px solid var(--su-line); border-radius: 12px; background: var(--su-surface); transition: width 0.2s ease; }
.su-pal-h { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; padding: 14px 10px 10px 14px; }
.su-pal-h b { display: block; font-size: 15px; font-weight: 600; color: var(--su-ink); }
.su-pal-h small { font-size: 12.5px; color: var(--su-ink-3); }
.su-pal-toggle { width: 30px; height: 30px; flex-shrink: 0; display: grid; place-items: center; border: none; border-radius: 8px; background: none; color: var(--su-ink-3); cursor: pointer; }
.su-pal-toggle:hover { background: var(--su-raised); }
.su-kit .su-pal .su-search { flex: none; width: auto; min-width: 0; height: 36px; margin: 0 12px 8px; border-radius: 9px; }
.su-kit .su-pal .su-search input { font-size: 13.5px; }
.su-pal-list { flex: 1; min-height: 0; overflow-y: auto; padding: 0 8px 10px; }
.su-pal-list h4 { margin: 0; padding: 10px 6px 4px; font-family: inherit; font-size: 11px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; color: var(--su-ink-3); }
.su-pal-item { display: flex; align-items: center; gap: 10px; padding: 6px; border-radius: 8px; font-size: 13.5px; color: var(--su-ink); cursor: grab; }
.su-pal-item:hover { background: var(--su-hover); }
.su-pal-item > span:nth-child(2) { flex: 1; min-width: 0; }
.su-pal-ic { width: 26px; height: 26px; flex-shrink: 0; display: grid; place-items: center; border-radius: 7px; color: #ffffff; font-size: 14px; }
.su-pal-grip { color: var(--su-ink-4); opacity: 0; }
.su-pal-item:hover .su-pal-grip { opacity: 1; }
.su-pal.closed .su-pal-item { justify-content: center; }
.su-pal.closed .su-pal-toggle { width: 100%; margin-top: 6px; }
.su-pal-list::-webkit-scrollbar { width: 6px; }
.su-pal-list::-webkit-scrollbar-track { background: transparent; }
.su-pal-list::-webkit-scrollbar-thumb { border-radius: 6px; background: transparent; }
.su-pal-list:hover::-webkit-scrollbar-thumb { background: var(--su-control); }
@supports not selector(::-webkit-scrollbar) { .su-pal-list { scrollbar-width: thin; scrollbar-color: var(--su-control) transparent; } }
.su-pal-orphans { padding: 10px 14px 12px; border-top: 1px solid var(--su-line); flex-shrink: 0; font-size: 12px; line-height: 1.4; color: var(--su-ink-2); }
.su-pal-orphans p { display: flex; gap: 7px; margin: 0 0 8px; }
.su-pal-orphans p svg { color: #e0a32e; margin-top: 1px; }
.su-pal-orphans div { display: flex; flex-wrap: wrap; gap: 4px; }
.su-pal-orphans button { max-width: 100%; padding: 3px 8px; border: 1px solid var(--su-line); border-radius: 99px; background: var(--su-surface); color: var(--su-ink-2); font-size: 12px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; cursor: pointer; }
.su-pal-orphans button:hover { border-color: var(--su-control); }
.su-pal-none { padding: 16px 8px; text-align: center; font-size: 13px; color: var(--su-ink-3); }
`;

const NodePalette = ({
  nodesDrawerWidth,
  setNodesDrawerWidth,
  selectedLog,
  showNodes,
  setShowNodes,
  orphanNodes = [],
  onGoToNode,
}) => {
  const [search, setSearch] = useState('');

  const filteredCategories = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return CATEGORIES;
    return CATEGORIES.map((cat) => ({
      ...cat,
      items: cat.items.filter((item) => item.label.toLowerCase().includes(query)),
    })).filter((cat) => cat.items.length > 0);
  }, [search]);

  const hasResults = filteredCategories.some((cat) => cat.items.length > 0);

  return (
    <div style={{ width: 0, flex: 'none' }}>
    <div
      className={`su-pal${showNodes ? '' : ' closed'}`}
      style={{
        width: `${showNodes ? Math.min(nodesDrawerWidth, 260) : nodesDrawerWidth}px`,
        visibility: selectedLog ? 'hidden' : 'visible',
      }}
    >
      <style>{PALETTE_CSS}</style>
      {showNodes ? (
        <>
          <div className="su-pal-h">
            <div>
              <b>Nodos</b>
              <small>Arrastra el que quieras usar</small>
            </div>
            <button
              type="button"
              className="su-pal-toggle"
              aria-label="Cerrar panel"
              data-tooltip-id="my-tooltip"
              data-tooltip-content="Cerrar panel"
              onClick={() => {
                setNodesDrawerWidth(60);
                setTimeout(() => setShowNodes(false), 150);
              }}
            >
              <LuPanelLeftClose className="su-ic" size={17} />
            </button>
          </div>
          <div className="su-search">
            <LuSearch className="su-ic" size={15} />
            <input
              type="text"
              id="nodePaletteSearch"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar nodo por nombre"
              aria-label="Buscar nodo por nombre"
            />
          </div>
        </>
      ) : (
        <button
          type="button"
          className="su-pal-toggle"
          aria-label="Abrir panel"
          data-tooltip-id="my-tooltip"
          data-tooltip-content="Abrir panel"
          onClick={() => {
            setShowNodes(true);
            setTimeout(() => setNodesDrawerWidth(300), 60);
          }}
        >
          <LuPanelLeftOpen className="su-ic" size={17} />
        </button>
      )}

      <div className="su-pal-list">
        {showNodes && !hasResults && <div className="su-pal-none">No hay nodos con ese nombre.</div>}
        {filteredCategories.map((cat) => (
          <div key={cat.label}>
            {showNodes && <h4>{cat.label}</h4>}
            {cat.items.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.type}
                  className="su-pal-item dndnode"
                  draggable
                  title={showNodes ? '' : item.label}
                  onDragStart={(event) => onDragStart(event, item.type)}
                >
                  <span className="su-pal-ic" style={{ backgroundColor: item.color }}>
                    <Icon />
                  </span>
                  {showNodes && (
                    <>
                      <span>{item.label}</span>
                      <LuGripVertical className="su-ic su-pal-grip" size={14} />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {showNodes && orphanNodes.length > 0 && (
        <div className="su-pal-orphans">
          <p>
            <LuAlertTriangle className="su-ic" size={14} />
            {orphanNodes.length === 1 ? 'Un nodo no cuelga de un evento y nunca se ejecuta.' : `${orphanNodes.length} nodos no cuelgan de un evento y nunca se ejecutan.`}
          </p>
          <div>
            {orphanNodes.slice(0, 8).map((node) => (
              <button key={node.id} type="button" onClick={() => onGoToNode?.(node.id)}>
                {node.data?.title || node.id}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default React.memo(NodePalette);
